import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Center,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  HStack,
  Icon,
  Image,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Spinner,
  Text,
  useDisclosure,
  useToast,
  VStack
} from "@chakra-ui/react";
import { RepeatIcon } from "@chakra-ui/icons";
import { FaMapMarkerAlt, FaUser, FaVideo } from "react-icons/fa";
import { io } from "socket.io-client";
import {
  fetchAlertImageWithAuth,
  formatDateTime,
  getSocketUrl,
  parseDetectionDescription
} from "./faceUtils";

const DetectionCard = ({ detection, onImageClick }) => {
  const meta = parseDetectionDescription(detection.description);
  const confidence = meta.confidence ?? meta.match_score ?? meta.score;

  return (
    <Box bg="white" borderRadius="lg" boxShadow="md" overflow="hidden">
      <Box position="relative">
        {detection.imageUrl ? (
          <Image
            src={detection.imageUrl}
            alt={detection.face?.name || "Detecção facial"}
            w="100%"
            h={{ base: "140px", md: "190px" }}
            objectFit="cover"
            cursor="pointer"
            onClick={() => onImageClick(detection.imageUrl)}
          />
        ) : (
          <Center h={{ base: "140px", md: "190px" }} bg="gray.100">
            <Text color="gray.500" fontSize="sm">Sem imagem</Text>
          </Center>
        )}
        {confidence !== undefined && confidence !== null && (
          <Badge position="absolute" top={2} right={2} colorScheme="purple">
            {typeof confidence === "number" && confidence <= 1
              ? `${(confidence * 100).toFixed(1)}%`
              : confidence}
          </Badge>
        )}
      </Box>

      <VStack align="stretch" spacing={2} p={4}>
        <HStack spacing={2}>
          <Icon as={FaUser} color="teal.500" />
          <Text fontWeight="bold" noOfLines={1}>
            {detection.face?.name || "Rosto desconhecido"}
          </Text>
        </HStack>

        <HStack spacing={2} align="start">
          <Icon as={FaVideo} color="blue.500" mt="2px" />
          <Box minW={0}>
            <Text fontSize="sm" noOfLines={1}>
              {detection.camera?.name || `Câmera #${detection.camera_id}`}
            </Text>
            {detection.camera?.location && (
              <HStack spacing={1} mt={1}>
                <Icon as={FaMapMarkerAlt} color="gray.400" boxSize={3} />
                <Text fontSize="xs" color="gray.600" noOfLines={2}>
                  {detection.camera.location}
                </Text>
              </HStack>
            )}
          </Box>
        </HStack>

        <Text fontSize="xs" color="gray.500">
          {formatDateTime(detection.alert_time || detection.createdAt)}
        </Text>
      </VStack>
    </Box>
  );
};

const FaceDetectionsTab = () => {
  const [detections, setDetections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, totalCount: 0 });
  const [filters, setFilters] = useState({ startDate: "", endDate: "", cameraId: "", faceId: "" });
  const [appliedFilters, setAppliedFilters] = useState({ startDate: "", endDate: "", cameraId: "", faceId: "" });
  const [selectedImage, setSelectedImage] = useState("");
  const imageModal = useDisclosure();
  const toast = useToast();
  const socketRef = useRef(null);
  const seenIdsRef = useRef(new Set());

  const buildQuery = useCallback((page) => {
    const params = new URLSearchParams({
      page: String(page),
      limit: "20"
    });
    if (appliedFilters.startDate) params.set("startDate", appliedFilters.startDate);
    if (appliedFilters.endDate) params.set("endDate", appliedFilters.endDate);
    if (appliedFilters.cameraId) params.set("cameraId", appliedFilters.cameraId);
    if (appliedFilters.faceId) params.set("faceId", appliedFilters.faceId);
    return params.toString();
  }, [appliedFilters]);

  const enrichDetections = useCallback(async (items) => {
    return Promise.all(items.map(async (item) => ({
      ...item,
      imageUrl: await fetchAlertImageWithAuth(item.alert_image)
    })));
  }, []);

  const fetchDetections = useCallback(async (page = 1, append = false) => {
    if (append) setLoadingMore(true);
    else setLoading(true);

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`/api/faces/detections?${buildQuery(page)}`, {
        headers: { Authorization: `Bearer ${token}` },
        cache: "no-cache"
      });

      if (!response.ok) throw new Error("Erro ao buscar detecções");

      const data = await response.json();
      const enriched = await enrichDetections(data.detections || []);

      enriched.forEach((item) => seenIdsRef.current.add(item.id));

      setDetections((current) => (append ? [...current, ...enriched] : enriched));
      setPagination({
        page: data.currentPage || page,
        totalPages: data.totalPages || 1,
        totalCount: data.totalCount || enriched.length
      });
    } catch (error) {
      toast({
        title: "Erro ao carregar detecções",
        description: error.message,
        status: "error",
        duration: 4000
      });
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [buildQuery, enrichDetections, toast]);

  useEffect(() => {
    fetchDetections(1, false);
  }, [fetchDetections]);

  useEffect(() => {
    const socketUrl = getSocketUrl();
    const socket = io(socketUrl, {
      path: "/socket.io/",
      transports: ["polling", "websocket"]
    });

    const handleFaceRecognized = async (payload) => {
      if (!payload?.face_id) return;
      if (seenIdsRef.current.has(payload.id)) return;

      seenIdsRef.current.add(payload.id);
      const [enriched] = await enrichDetections([payload]);
      setDetections((current) => [enriched, ...current]);
      setPagination((current) => ({
        ...current,
        totalCount: current.totalCount + 1
      }));

      toast({
        title: "Nova detecção facial",
        description: enriched.face?.name
          ? `${enriched.face.name} em ${enriched.camera?.name || "câmera"}`
          : "Rosto reconhecido pelas câmeras",
        status: "info",
        duration: 4000
      });
    };

    socket.on("face-recognized", handleFaceRecognized);
    socketRef.current = socket;

    return () => {
      socket.off("face-recognized", handleFaceRecognized);
      socket.disconnect();
      socketRef.current = null;
    };
  }, [enrichDetections, toast]);

  const openImageModal = (imageUrl) => {
    setSelectedImage(imageUrl);
    imageModal.onOpen();
  };

  const activeFiltersCount = useMemo(
    () => Object.values(appliedFilters).filter((value) => String(value || "").trim()).length,
    [appliedFilters]
  );

  return (
    <Box>
      <Box bg="white" p={4} borderRadius="lg" boxShadow="sm" mb={4}>
        <SimpleGrid columns={{ base: 1, md: 4 }} spacing={3}>
          <FormControl>
            <FormLabel fontSize="sm">Data inicial</FormLabel>
            <Input
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters((prev) => ({ ...prev, startDate: e.target.value }))}
            />
          </FormControl>
          <FormControl>
            <FormLabel fontSize="sm">Data final</FormLabel>
            <Input
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters((prev) => ({ ...prev, endDate: e.target.value }))}
            />
          </FormControl>
          <FormControl>
            <FormLabel fontSize="sm">ID da câmera</FormLabel>
            <Input
              placeholder="Opcional"
              value={filters.cameraId}
              onChange={(e) => setFilters((prev) => ({ ...prev, cameraId: e.target.value }))}
            />
          </FormControl>
          <FormControl>
            <FormLabel fontSize="sm">ID do rosto</FormLabel>
            <Input
              placeholder="Opcional"
              value={filters.faceId}
              onChange={(e) => setFilters((prev) => ({ ...prev, faceId: e.target.value }))}
            />
          </FormControl>
        </SimpleGrid>

        <Flex mt={4} gap={2} wrap="wrap" align="center">
          <Button colorScheme="teal" onClick={() => setAppliedFilters({ ...filters })} leftIcon={<RepeatIcon />}>
            Aplicar filtros
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              const empty = { startDate: "", endDate: "", cameraId: "", faceId: "" };
              setFilters(empty);
              setAppliedFilters(empty);
            }}
          >
            Limpar
          </Button>
          <Badge colorScheme="blue">{pagination.totalCount} detecções</Badge>
          {activeFiltersCount > 0 && (
            <Badge colorScheme="orange">{activeFiltersCount} filtro(s) ativo(s)</Badge>
          )}
        </Flex>
      </Box>

      {loading ? (
        <Flex justify="center" py={16}>
          <Spinner size="xl" color="teal.500" />
        </Flex>
      ) : detections.length === 0 ? (
        <Center py={16} bg="white" borderRadius="lg" boxShadow="sm" flexDirection="column" gap={3}>
          <Text color="gray.500" fontSize="lg">Nenhuma detecção facial encontrada</Text>
          <Text color="gray.400" fontSize="sm" textAlign="center" maxW="480px">
            As detecções aparecem aqui quando uma câmera com reconhecimento facial identifica um rosto cadastrado.
          </Text>
        </Center>
      ) : (
        <>
          <Grid
            templateColumns={{ base: "repeat(2, minmax(0, 1fr))", lg: "repeat(auto-fill, minmax(280px, 1fr))" }}
            gap={{ base: 3, md: 5 }}
          >
            {detections.map((detection) => (
              <GridItem key={detection.id}>
                <DetectionCard detection={detection} onImageClick={openImageModal} />
              </GridItem>
            ))}
          </Grid>

          {pagination.page < pagination.totalPages && (
            <Flex justify="center" mt={6}>
              <Button
                onClick={() => fetchDetections(pagination.page + 1, true)}
                isLoading={loadingMore}
                loadingText="Carregando"
              >
                Carregar mais
              </Button>
            </Flex>
          )}
        </>
      )}

      <Modal isOpen={imageModal.isOpen} onClose={imageModal.onClose} size="4xl" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Imagem da detecção</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {selectedImage && (
              <Image src={selectedImage} alt="Detecção facial" w="100%" maxH="75vh" objectFit="contain" mx="auto" />
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default FaceDetectionsTab;
